import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import { SxProps, Theme } from '@mui/material/styles';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import { isNil } from 'lodash-es';
import { FieldValues, useController, UseControllerProps } from 'react-hook-form';

import Label from './Label';
import { TextFieldInputSlotProps } from './TextFieldInputSlotProps';

type Props<T extends FieldValues> = UseControllerProps<T> &
  Pick<TextFieldProps, 'placeholder' | 'maxRows' | 'minRows' | 'rows'> & {
    dataUi?: string;
    isDisabled?: boolean;
    isRequired?: boolean;
    label?: React.ReactNode;
    slotProps?: TextFieldInputSlotProps;
    sx?: SxProps<Theme>;
  };

export default function TextInput<T extends FieldValues>({
  control,
  dataUi,
  isDisabled,
  isRequired,
  label,
  maxRows,
  minRows,
  name,
  placeholder,
  rows,
  rules,
  slotProps,
  sx,
}: Props<T>) {
  const { field, fieldState } = useController({ control, name, rules });
  const error = fieldState.error?.message ?? fieldState.error?.root?.message;
  const isMultiline = !isNil(maxRows) || !isNil(minRows) || !isNil(rows);

  return (
    <FormControl
      disabled={isDisabled}
      error={Boolean(error)}
      fullWidth
      sx={[
        { display: 'flex', flexDirection: 'column', gap: 0.25 },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {label && <Label isRequired={isRequired}>{label}</Label>}

      <TextField
        className={isRequired ? 'required' : ''}
        disabled={isDisabled}
        error={Boolean(error)}
        fullWidth
        id={name}
        maxRows={maxRows}
        minRows={minRows}
        multiline={isMultiline}
        onChange={field.onChange}
        placeholder={placeholder}
        rows={rows}
        slotProps={{
          ...slotProps,
          htmlInput: { ...slotProps?.htmlInput, ...(dataUi && { 'data-ui': dataUi }) },
        }}
        type="text"
        value={field.value}
      />

      {error && (
        <FormHelperText sx={{ m: 0, fontSize: '10px', fontWeight: 700 }}>
          {error}
        </FormHelperText>
      )}
    </FormControl>
  );
}
